import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { i18nLike } from "#web/services/i18nLike";
import { validateEmail } from "#web/utils/validateEmail";
import {
  Banner,
  BlockStack,
  Button,
  InlineError,
  InlineStack,
  SkeletonBodyText,
  SkeletonDisplayText,
  Tag,
  TextField,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

const en = {
  emailIsNotValid: "Email is not valid",
  error: {
    description: "Please try again later. If the issue persists, please contact support.",
    title: "Error loading email notification",
  },
  notIncludedBanner: {
    description: "Your Shopify email address is used. Upgrade to set email alert addresses.",
    title: "Not included in the freemium plan",
  },
  placeholder: "example@email.com",
};

interface EmailAlertProps {
  emails: string[];
  setEmails: (emails: string[]) => void;
}

export const EmailAlert = ({ emails, setEmails }: EmailAlertProps) => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [alertEmailInput, setAlertEmailInput] = useState<string>("");
  const handleTextFieldChange = useCallback((value: string) => {
    setAlertEmailInput(value);
  }, []);

  const [emailInputError, setInputError] = useState(false);

  const appendEmail = useCallback(() => {
    if (validateEmail(alertEmailInput)) {
      console.log("alertEmailInput", alertEmailInput);
      setEmails([...emails, alertEmailInput]);
      setAlertEmailInput("");
      setInputError(false);
    } else {
      setInputError(true);
    }
  }, [alertEmailInput, emails, setEmails]);

  const removeEmail = useCallback(
    (email: string) => {
      setEmails(emails.filter((e) => e !== email));
    },
    [emails, setEmails],
  );

  if (shopError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  if (shopFetching) {
    return (
      <BlockStack gap="300" id="email-alert-section">
        <SkeletonDisplayText />
        <SkeletonBodyText lines={2} />
      </BlockStack>
    );
  }

  if (shop?.subscribedTo("freemium")) {
    return (
      <Banner title={t("notIncludedBanner.title")} tone="warning">
        {t("notIncludedBanner.description")}
      </Banner>
    );
  }

  return (
    <BlockStack gap="400" id="email-alert-section">
      <div className="alert-settings" style={{ maxWidth: "48rem" }}>
        <TextField
          autoComplete="off"
          label={""}
          onChange={handleTextFieldChange}
          placeholder={t("placeholder")}
          type="email"
          value={alertEmailInput}
        />
        <Button onClick={appendEmail}>+</Button>
      </div>
      {emailInputError ? <InlineError fieldID="alertEmailInput" message={t("emailIsNotValid")} /> : null}
      {emails.length > 0 && (
        <InlineStack gap="100">
          {emails.map((email) => (
            <Tag
              key={email}
              onRemove={() => {
                removeEmail(email);
              }}
            >
              {email}
            </Tag>
          ))}
        </InlineStack>
      )}
    </BlockStack>
  );
};
